import { useState } from "react";
import type { KeyboardEvent } from "react";

// StarRating — the post-session 1–5 star control (rates the session, not the
// therapist). Hover / focus previews the score (visual on-state through N +
// live word) WITHOUT committing; click / Enter / Space commits via onChange.
// `value` 0 = unset (emptyPrompt shows); `helper` renders always. The `data-on`
// attribute is the observable preview hook (tests + CSS).

export type RatingWords = Partial<Record<1 | 2 | 3 | 4 | 5, string>>;

export interface StarRatingProps {
    /** Committed value; 0 = unset. */
    value: number;
    onChange: (n: number) => void;
    /** Live word per score (e.g. 1 "Rough" … 5 "Really helpful"). */
    words?: RatingWords;
    /** Always-visible helper line (e.g. "Honest is more useful than kind."). */
    helper?: string;
    /** Word-slot text while nothing is shown (value 0, no hover). */
    emptyPrompt?: string;
}

const SCORES = [1, 2, 3, 4, 5] as const;

/** The live word / empty-prompt / helper footer under a 1–5 scale — shared by
 *  StarRating and WarmthDots (identical anatomy, per contracts §5). */
export function RatingCaption({
    shown,
    words,
    helper,
    emptyPrompt,
}: {
    shown: number;
    words?: RatingWords;
    helper?: string;
    emptyPrompt?: string;
}) {
    return (
        <div className="flex min-h-[52px] flex-col items-center gap-1 text-center">
            {/* The prompt and the word share ONE type style (h2 serif), so tapping a star swaps
                the words in place instead of jumping from a small sans line to a big serif one.
                Only the colour marks the prompt as not-yet-an-answer. */}
            {shown ? (
                <span className="[font:var(--type-h2)] text-[var(--text-primary)]">
                    {words?.[shown as 1 | 2 | 3 | 4 | 5] ?? ""}
                </span>
            ) : emptyPrompt ? (
                <span className="[font:var(--type-h2)] text-tertiary">{emptyPrompt}</span>
            ) : null}
            {helper ? <p className="m-0 [font:var(--type-caption)] text-secondary">{helper}</p> : null}
        </div>
    );
}

// Star glyph as an SVG (not the ★ text char). Solid fills, no outline: ON is the deeper
// yellow mood colour (--mood-yellow-bd, the check-in circle's border), OFF the DS grey
// (--border), so an unset rating reads as the same quiet grey it always has.
function StarGlyph({ on }: { on: boolean }) {
    return (
        // 48px on a phone (five fit a 327px content column), 56px from `sm` up.
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-12 w-12 sm:h-14 sm:w-14">
            <path
                d="M12 2.5l2.95 5.98 6.6.96-4.78 4.66 1.13 6.58L12 17.58l-5.9 3.1 1.13-6.58L2.45 9.44l6.6-.96L12 2.5z"
                className={[
                    "transition-[fill] duration-[var(--dur-base)] ease-[var(--ease-out)]",
                    on ? "fill-mood-yellow-bd" : "fill-border",
                ].join(" ")}
            />
        </svg>
    );
}

export function StarRating({ value, onChange, words, helper, emptyPrompt }: StarRatingProps) {
    const [preview, setPreview] = useState(0);
    const shown = preview || value;

    function commitOnKey(event: KeyboardEvent<HTMLButtonElement>, n: number) {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onChange(n);
        }
    }

    return (
        <div className="flex flex-col items-center gap-4">
            <div className="flex gap-2 sm:gap-4" onMouseLeave={() => setPreview(0)}>
                {SCORES.map((n) => {
                    const on = n <= shown;
                    return (
                        <button
                            key={n}
                            type="button"
                            aria-label={`${n} of 5`}
                            aria-pressed={n === value}
                            data-on={on ? "true" : "false"}
                            onMouseEnter={() => setPreview(n)}
                            onFocus={() => setPreview(n)}
                            onBlur={() => setPreview(0)}
                            onClick={() => onChange(n)}
                            onKeyDown={(e) => commitOnKey(e, n)}
                            className={[
                                "cursor-pointer border-none bg-transparent px-1 py-2 transition-[color,transform]",
                                on ? "scale-105" : "",
                                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent rounded-[var(--radius-sm)]",
                            ].join(" ")}
                        >
                            <StarGlyph on={on} />
                        </button>
                    );
                })}
            </div>
            <RatingCaption shown={shown} words={words} helper={helper} emptyPrompt={emptyPrompt} />
        </div>
    );
}
